import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthGuard } from "@/components/AuthGuard";
import { AppLayout } from "@/components/layout/AppLayout";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { TruckLoader } from "@/components/ui/truck-loader";
import { useAuth } from "@/lib/auth";
import { getDefaultRoute } from "@/lib/navigation";
import type { UserRole } from "@workspace/api-client";

const Login = lazy(() => import("@/pages/login"));
const Programacion = lazy(() => import("@/pages/programacion"));
const Servicios = lazy(() => import("@/pages/servicios"));
const Clientes = lazy(() => import("@/pages/clientes"));
const Negocios = lazy(() => import("@/pages/negocios"));
const Vehiculos = lazy(() => import("@/pages/vehiculos"));
const Personal = lazy(() => import("@/pages/personal"));
const TiposServicio = lazy(() => import("@/pages/tipos-servicio"));
const TiposResiduo = lazy(() => import("@/pages/tipos-residuo"));
const TiposFlota = lazy(() => import("@/pages/tipos-flota"));
const Opcionales = lazy(() => import("@/pages/opcionales"));
const Monitor = lazy(() => import("@/pages/monitor"));
const Certificaciones = lazy(() => import("@/pages/certificaciones"));
const Usuarios = lazy(() => import("@/pages/usuarios"));
const Configuracion = lazy(() => import("@/pages/configuracion"));
const Feedback = lazy(() => import("@/pages/feedback"));
const Auditoria = lazy(() => import("@/pages/auditoria"));
const PlantasDescarga = lazy(() => import("@/pages/plantas-descarga"));
const Reportes = lazy(() => import("@/pages/reportes"));
const Disponibilidad = lazy(() => import("@/pages/disponibilidad"));
const RolesPermisos = lazy(() => import("@/pages/roles-permisos"));
const NotFound = lazy(() => import("@/pages/not-found"));
const CambiarAcceso = lazy(() => import("@/pages/cambiar-acceso"));
const Novedades = lazy(() => import("@/pages/novedades"));

const ADMIN: UserRole[] = ["admin"];
const ADMIN_PLUS: UserRole[] = ["superadmin" as UserRole, "admin"];
const LOGISTICA: UserRole[] = ["admin", "logistica", "gerencia"];

const AUDITORIA: UserRole[] = ["superadmin" as UserRole, "admin", "logistica", "gerencia"];
// Todos los roles del panel web menos chofer (usa su propia app).
const NOVEDADES: UserRole[] = ["superadmin" as UserRole, "admin", "logistica", "gerencia", "comercial" as UserRole];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function RoleHomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={getDefaultRoute(user?.rol)} replace />;
}

function App() {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, "") || "";
  const loadingFallback = <TruckLoader />;

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter basename={base}>
          <Suspense fallback={loadingFallback}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/cambiar-acceso" element={<CambiarAcceso />} />
              <Route
                path="/"
                element={
                  <AuthGuard>
                    <FeatureFlagsProvider>
                      <AppLayout />
                    </FeatureFlagsProvider>
                  </AuthGuard>
                }
              >
                <Route index element={<RoleHomeRedirect />} />
                <Route path="programacion" element={<AuthGuard allowedRoles={LOGISTICA}><Programacion /></AuthGuard>} />
                <Route path="monitor" element={<AuthGuard allowedRoles={LOGISTICA}><Monitor /></AuthGuard>} />
                <Route path="servicios" element={<AuthGuard allowedRoles={LOGISTICA}><Servicios /></AuthGuard>} />
                <Route path="clientes" element={<AuthGuard allowedRoles={LOGISTICA}><Clientes /></AuthGuard>} />
                <Route path="negocios" element={<AuthGuard allowedRoles={LOGISTICA}><Negocios /></AuthGuard>} />
                <Route path="vehiculos" element={<AuthGuard allowedRoles={["admin", "logistica", "gerencia"]}><Vehiculos /></AuthGuard>} />
                <Route path="acoplados" element={<AuthGuard allowedRoles={["admin", "logistica", "gerencia"]}><Vehiculos /></AuthGuard>} />
                <Route path="personal" element={<AuthGuard allowedRoles={ADMIN}><Personal /></AuthGuard>} />
                <Route path="tipos-servicio" element={<AuthGuard allowedRoles={ADMIN}><TiposServicio /></AuthGuard>} />
                <Route path="tipos-residuo" element={<AuthGuard allowedRoles={ADMIN}><TiposResiduo /></AuthGuard>} />
                <Route path="tipos-flota" element={<AuthGuard allowedRoles={ADMIN}><TiposFlota /></AuthGuard>} />
                <Route path="opcionales" element={<AuthGuard requiredPermission="optionals:read"><Opcionales /></AuthGuard>} />
                <Route path="reportes" element={<AuthGuard allowedRoles={["superadmin" as UserRole, "admin", "logistica", "gerencia", "comercial" as UserRole]}><Reportes /></AuthGuard>} />
                <Route path="certificaciones" element={<AuthGuard allowedRoles={ADMIN_PLUS}><Certificaciones /></AuthGuard>} />

                <Route path="disponibilidad" element={<AuthGuard allowedRoles={["admin", "logistica", "gerencia"]}><Disponibilidad /></AuthGuard>} />
                <Route path="plantas-descarga" element={<AuthGuard allowedRoles={LOGISTICA}><PlantasDescarga /></AuthGuard>} />
                <Route path="feedback" element={<AuthGuard requiredPermission="feedback:read"><Feedback /></AuthGuard>} />
                <Route path="auditoria" element={<AuthGuard allowedRoles={AUDITORIA}><Auditoria /></AuthGuard>} />
                <Route path="novedades" element={<AuthGuard allowedRoles={NOVEDADES}><Novedades /></AuthGuard>} />
                <Route path="usuarios" element={<AuthGuard requiredPermission="users:read"><Usuarios /></AuthGuard>} />
                <Route path="configuracion" element={<AuthGuard requiredPermission="feature_flags:manage"><Configuracion /></AuthGuard>} />
                <Route path="roles-permisos" element={<AuthGuard requiredPermission="roles_perms:view"><RolesPermisos /></AuthGuard>} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
